import React, { useEffect, useState } from "react";
import {
  Modal,
  Card,
  Row,
  Col,
  Typography,
  Avatar,
  Badge,
  Button,
  Divider,
  Statistic,
  Tag,
  Table,
  message,
  Input,
  Select,
  Space,
  Switch,
} from "antd";
import {
  UserOutlined,
  EyeOutlined,
  WalletOutlined,
  LoginOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  IdcardOutlined,
  DownloadOutlined,
  SettingOutlined,
  FileTextOutlined,
  DollarOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { downloadQrAsPng, downloadQrAsPdf, QrDownloadModal } from "./QrCode";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import { DollarSign, Infinity, RotateCcwSquare } from "lucide-react";

const { Title, Text } = Typography;
const { Option } = Select;

const SurfaceCard = ({ children, bg, border }) => (
  <Card
    bordered={false}
    style={{
      height: "100%",
      borderRadius: 20,
      background: bg,
      border,
      boxShadow: "0 12px 30px rgba(0,0,0,.06)",
    }}
  >
    {children}
  </Card>
);

const InfoItem = ({ icon, label, value }) => (
  <div
    style={{
      display: "flex",
      gap: 12,
      alignItems: "flex-start",
      marginBottom: 14,
    }}
  >
    {icon && (
      <span
        style={{
          color: "#8c8c8c",
          fontSize: 16,
          marginTop: 2,
        }}
      >
        {icon}
      </span>
    )}

    <div>
      <Text type="secondary" strong style={{ fontSize: 15 }}>
        {label}
      </Text>
      <div style={{ fontWeight: 500 }}>{value || "N/A"}</div>
    </div>
  </div>
);

const SectionTitle = ({ icon, title }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    <span style={{ fontSize: 18, color: "#1677ff" }}>{icon}</span>
    <Title level={5} style={{ margin: 0 }}>
      {title}
    </Title>
  </div>
);

const MerchantProfileModal = ({
  open,
  onClose,
  merchant,
  onViewWallet,
  onUpdateAdminComments,
  adminCommentsUpdateLoading,
  hasTrustAccountPermission,
}) => {
  const { request } = useRequest();
  const [adminComments, setAdminComments] = useState("");
  const [isEditingAdminComments, setIsEditingAdminComments] = useState(false);
  
  // Manual Subscription states
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [packages, setPackages] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [assignLoading, setAssignLoading] = useState(false);
  const [isMonthlyTrial, setIsMonthlyTrial] = useState(false);
  const [monthlyTrialDays, setMonthlyTrialDays] = useState(0);
  const [isTransactionTrial, setIsTransactionTrial] = useState(false);
  const [transactionTrialDays, setTransactionTrialDays] = useState(0);

  // QR Code download modal states
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [selectedWalletForQr, setSelectedWalletForQr] = useState(null);

  const userProfile = JSON.parse(localStorage.getItem("user") || "{}");
  const subPerm = userProfile?.role?.module_permissions?.find(p => p.module === "subscription");
  const canAssignSubscription = subPerm?.can_create || false;

  useEffect(() => {
    setAdminComments(merchant?.admin_comments || "");
    setIsEditingAdminComments(false);
    if (!open) {
      setAssignModalVisible(false);
      setSelectedPackageId(null);
      setIsMonthlyTrial(false);
      setMonthlyTrialDays(0);
      setIsTransactionTrial(false);
      setTransactionTrialDays(0);
    }
  }, [merchant, open]);

  const handleDownloadQr = (wallet) => {
    const qrCodeUrl = wallet?.qrCode;
    if (!qrCodeUrl || qrCodeUrl === "N/A") {
      message.warning("QR code not available for this wallet");
      return;
    }
    setSelectedWalletForQr(wallet);
    setQrModalVisible(true);
  };

  if (!merchant) return null;

  const fetchActivePackages = () => {
    request({
      url: "/api/admin/subscription",
      method: "GET",
      onSuccess: (res) => {
        console.log("SUBSCRIPTION API RESPONSE", res);

        const list = Array.isArray(res?.data)
          ? res.data
          : (res?.data?.data || []);

        console.log("FILTERED PACKAGES", list);

        setPackages(list.filter(p => p.is_active || p.is_published));
      }
    });
  };

  const handlePackageChange = (val) => {
    setSelectedPackageId(val);
    const pkg = packages.find(p => p.id === val);
    if (pkg) {
      setIsMonthlyTrial(pkg.is_monthly_trial || false);
      setMonthlyTrialDays(pkg.monthly_trial_days || 0);
      setIsTransactionTrial(pkg.is_transaction_trial || false);
      setTransactionTrialDays(pkg.transaction_trial_days || 0);
    }
  };

  const handleAssignSubscription = () => {
    if (!selectedPackageId) {
      ShowToast("Please select a subscription package", Severty.ERROR);
      return;
    }
    setAssignLoading(true);
    request({
      url: "/api/admin/assign-subscription",
      method: "POST",
      data: {
        type: "merchant",
        id: merchant.id,
        package_id: selectedPackageId,
        is_monthly_trial: isMonthlyTrial,
        monthly_trial_days: monthlyTrialDays,
        is_transaction_trial: isTransactionTrial,
        transaction_trial_days: transactionTrialDays,
      },
      onSuccess: () => {
        ShowToast("Subscription manually assigned successfully", Severty.SUCCESS);
        setAssignLoading(false);
        setAssignModalVisible(false);
        onClose(); // close profile modal to trigger refresh on list
      },
      onError: () => {
        ShowToast("Failed to assign subscription", Severty.ERROR);
        setAssignLoading(false);
      }
    });
  };

  const primaryUser = merchant?.users?.[0]?.user ?? null;
  const merchantLastLogin = primaryUser?.last_login;
  const merchantJoinDate = merchant?.created_at ?? primaryUser?.join_date;
  const merchantAddress = merchant?.address ?? "N/A";
  const savedAdminComments = merchant?.admin_comments || "";
  const isAdminCommentsChanged = adminComments !== savedAdminComments;

  const handleSaveAdminComments = async () => {
    const didUpdate = await onUpdateAdminComments?.(merchant, adminComments);
    if (didUpdate) {
      setIsEditingAdminComments(false);
    }
  };

  const isActive = merchant.is_active ? true : false;
  const walletDetails = merchant.wallet ?? [];
  const merchantUsers = merchant?.users ?? [];

  const normalizedWallets = Array.isArray(walletDetails)
    ? walletDetails
    : walletDetails
      ? [walletDetails]
      : [];

  const totalWalletBalance = normalizedWallets.reduce(
    (sum, wallet) => sum + Number(wallet?.balance || 0),
    0,
  );

  const totalWalletFees = normalizedWallets.reduce(
    (sum, wallet) => sum + Number(wallet?.total_fees ?? wallet?.pending_fees ?? 0),
    0,
  );

  const totalAvailableBalance = normalizedWallets.reduce(
    (sum, wallet) =>
      sum +
      Number(
        wallet?.available_balance ??
          Math.max(0, Number(wallet?.balance || 0) - Number(wallet?.pending_fees || 0)),
      ),
    0,
  );

  const walletRows = normalizedWallets.map((wallet, index) => {
    const bal = Number(wallet?.balance ?? 0);
    const fees = Number(wallet?.total_fees ?? wallet?.pending_fees ?? 0);
    const avail =
      wallet?.available_balance !== undefined
        ? Number(wallet.available_balance)
        : Math.max(0, bal - fees);

    return {
      key: wallet?.wallet_id ?? index,
      walletId: wallet?.wallet_id ?? "N/A",
      balance: bal,
      totalFees: fees,
      availableBalance: avail,
      accountId: wallet?.account_id ?? "N/A",
      payId: wallet?.pay_id ?? "N/A",
      qrCode: wallet?.qr_code ?? null,
    };
  });

  const walletColumns = [
    {
      title: "Wallet ID",
      dataIndex: "walletId",
      key: "walletId",
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      render: (value) => `NZ$ ${Number(value || 0).toFixed(2)}`,
    },
    {
      title: "Total Fees",
      dataIndex: "totalFees",
      key: "totalFees",
      render: (value) => `NZ$ ${Number(value || 0).toFixed(2)}`,
    },
    {
      title: "Available to Withdraw",
      dataIndex: "availableBalance",
      key: "availableBalance",
      render: (value) => (
        <span style={{ color: "#389e0d", fontWeight: "bold" }}>
          NZ$ {Number(value || 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Account ID",
      dataIndex: "accountId",
      key: "accountId",
    },
    {
      title: "Pay ID",
      dataIndex: "payId",
      key: "payId",
    },
    {
      title: "QR Code",
      key: "qrCode",
      align: "center",
      render: (wallet) => (
          <Button
            size="middle"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => {
              handleDownloadQr(wallet);
            }}
          >
            Download
        </Button>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Button
          size="middle"
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => onViewWallet?.(record)}
        >
          View
        </Button>
      ),
    },
  ];

  const userRows = merchantUsers.map((userEntry, index) => {
    const user = userEntry?.user ?? {};
    const fullName = [user?.first_name, user?.middle_name, user?.last_name]
      .filter(Boolean)
      .join(" ");

    return {
      key: user?.id ?? index,
      name: fullName || "N/A",
      email: user?.email ?? "N/A",
      mobile: user?.mobile
        ? `${user?.country_code ?? ""} ${user.mobile}`.trim()
        : "N/A",
      role: userEntry?.role ?? "N/A",
      isPrimary: !!userEntry?.is_primary,
      status: user?.status ?? "N/A",
      joinedAt: userEntry?.joined_at ?? null,
    };
  });

  const usersColumns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Mobile", dataIndex: "mobile", key: "mobile" },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (value) => <Tag color="blue">{value}</Tag>,
    },
    {
      title: "Primary",
      dataIndex: "isPrimary",
      key: "isPrimary",
      render: (value) => (
        <Badge status={value ? "success" : "default"} text={value ? "Yes" : "No"} />
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => (
        <Badge status={value === "Active" ? "success" : "default"} text={value} />
      ),
    },
    {
      title: "Joined At",
      dataIndex: "joinedAt",
      key: "joinedAt",
      render: (value) =>
        value ? moment(value).format("DD MMM YYYY, hh:mm A") : "N/A",
    },
  ];

  const invoicesColumns = [
    { title: "Invoice Number", dataIndex: "invoice_number", key: "invoice_number", render: (val) => <Text strong>{val}</Text> },
    { title: "Type", dataIndex: "invoice_type", key: "invoice_type", render: (val) => <Tag color={val === "SUBSCRIPTION_FEE" ? "blue" : "purple"}>{val}</Tag> },
    { title: "Amount (NZD)", dataIndex: "total_amount", key: "total_amount", render: (val) => `$${val}` },
    { title: "Status", dataIndex: "status", key: "status", render: (val) => <Tag color={val === "SENT" ? "green" : "orange"}>{val}</Tag> },
    { title: "Due Date", dataIndex: "due_date", key: "due_date" },
    { title: "PDF", dataIndex: "pdf_url", key: "pdf_url", render: (val) => val ? <a href={val} target="_blank" rel="noreferrer">Download</a> : "N/A" }
  ];

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width="80vw"
        centered
        destroyOnClose
        zIndex={1000}
        styles={{
          content: {
            padding: 0,
            borderRadius: 24,
            overflow: "hidden",
            background: "#f5f7fb",
          },
        }}
      >
        {/* ===== HERO HEADER ===== */}
        <div
          style={{
            padding: "12px 16px",
            background: "linear-gradient(135deg, #eef3ff 0%, #f9fcff 100%)",
            borderBottom: "1px solid #e4ebf5",
          }}
        >
          <Row justify="space-between" align="middle" className="mb-3" gutter={[16, 16]} >
            <Col>
              <div style={{ display: "flex", gap: 20, flexWrap: "wrap", gap: "10px" }}>
                {merchant?.business_logo ? (
                  <div
                    style={{
                      width: 90,
                      height: 90,
                      minWidth: 90,
                      minHeight: 90,
                      borderRadius: "50%",
                      overflow: "hidden",
                      border: "4px solid #fff",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      backgroundColor: "#e6f0ff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={merchant.business_logo}
                      alt={merchant.business_name || "Business Logo"}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center",
                        display: "block",
                      }}
                    />
                  </div>
                ) : (
                  <Avatar
                    size={90}
                    icon={<UserOutlined />}
                    style={{
                      background: "#e6f0ff",
                      color: "#2952cc",
                      border: "4px solid #fff",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "36px",
                    }}
                  />
                )}
                <div>
                  <Title level={2} style={{ marginBottom: 4 }}>
                    <IdcardOutlined style={{ marginRight: 10, color: "#1677ff" }} />
                    {merchant.business_name}
                  </Title>
                  <div style={{ marginTop: 8 }}>
                    <Tag color="blue">{merchant.merchant_code}</Tag>
                    {merchant.is_admin_wallet && (
                      <Tag color="gold" style={{ marginLeft: 8 }}>Designated Admin Wallet</Tag>
                    )}
                    <Badge
                      status={isActive ? "success" : "error"}
                      text={isActive ? "Active" : "Inactive"}
                      style={{ marginLeft: 12 }}
                    />
                  </div>
                </div>
              </div>
            </Col>

          </Row>
        </div>

        {/* ===== STATS STRIP ===== */}
        <div style={{ padding: "12px 16px" }}>
          <Row gutter={[16, 16]}>
            <Col span={24} sm={12} lg={6}>
              <SurfaceCard bg="linear-gradient(135deg, #f0f9ff, #e6fffb)" border="1px solid #cceeff">
                <Statistic
                  title="Total Wallet Balance"
                  prefix={<WalletOutlined style={{ color: "#13c2c2" }} />}
                  value={`NZ$ ${Number(totalWalletBalance).toFixed(2)}`}
                />
              </SurfaceCard>
            </Col>

            <Col span={24} sm={12} lg={6}>
              <SurfaceCard bg="linear-gradient(135deg, #fffbe6, #fff1f0)" border="1px solid #ffe58f">
                <Statistic
                  title="Total Fees (Pending)"
                  prefix={<DollarOutlined style={{ color: "#faad14" }} />}
                  value={`NZ$ ${Number(totalWalletFees).toFixed(2)}`}
                />
              </SurfaceCard>
            </Col>

            <Col span={24} sm={12} lg={6}>
              <SurfaceCard bg="linear-gradient(135deg, #f6ffed, #d9f7be)" border="1px solid #b7eb8f">
                <Statistic
                  title="Available to Withdraw"
                  prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
                  value={`NZ$ ${Number(totalAvailableBalance).toFixed(2)}`}
                  valueStyle={{ color: "#389e0d", fontWeight: "bold" }}
                />
              </SurfaceCard>
            </Col>

            <Col span={24} sm={12} lg={6}>
              <SurfaceCard bg="linear-gradient(135deg, #f0f9ff, #e6fffb)" border="1px solid #cceeff">
                <Statistic
                  title="Member Since"
                  prefix={<CalendarOutlined style={{ color: "#fa8c16" }} />}
                  value={moment(merchantJoinDate).format("DD MMM YYYY")}
                />
              </SurfaceCard>
            </Col>
          </Row>
        </div>

        {/* ===== MAIN CONTENT ===== */}
        <div style={{ padding: "0 16px 16px" }}>
          <Row gutter={[24, 24]}>
            {/* KYB / Merchant Info */}
            <Col span={24}>
              <SurfaceCard bg="linear-gradient(135deg, #f0f9ff, #e6fffb)" border="1px solid #cceeff">
                <SectionTitle icon={<UserOutlined />} title="Business Information" />
                <Divider />

                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <InfoItem label="Business Name" value={merchant.business_name} />
                    <InfoItem label="Business Phone" value={merchant.business_mobile || primaryUser?.mobile || "N/A"} />
                    <InfoItem label="Business Email" value={primaryUser?.email || "N/A"} />
                    <InfoItem label="Address" value={merchantAddress} />
                  </Col>
                  <Col xs={24} md={12}>
                    <InfoItem label="Invoicing Name" value={merchant.invoicing_name} />
                    <InfoItem label="GST Number" value={merchant.gst_number} />
                    <InfoItem label="Invoicing Email" value={merchant.invoicing_email} />
                  </Col>
                </Row>
                
                {merchant.merchant_details && (
                  <>
                    <Divider />
                    <Row gutter={[16, 16]}>
                      <Col xs={24} md={12}>
                        <InfoItem label="Business Since" value={merchant.merchant_details.business_since?.replace(/_/g, ' ') || "N/A"} />
                        <InfoItem label="Expected Volume" value={merchant.merchant_details.transaction_amount_expected?.replace(/_/g, ' ') || "N/A"} />
                      </Col>
                      <Col xs={24} md={12}>
                        <InfoItem label="Business Industry" value={merchant.merchant_details.business_description?.replace(/_/g, ' ') || "N/A"} />
                      </Col>
                    </Row>
                  </>
                )}
              </SurfaceCard>
            </Col>

            {/* Admin Internal Comments */}
            <Col span={24}>
              <SurfaceCard bg="linear-gradient(135deg, #f0f9ff, #e6fffb)" border="1px solid #cceeff">
                <SectionTitle icon={<UserOutlined />} title="Admin Operations & Comments" />
                <Divider />

                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary" strong style={{ display: "block", marginBottom: 8 }}>
                    Internal Administrative Comments
                  </Text>
                  {isEditingAdminComments ? (
                    <Input.TextArea
                      rows={4}
                      value={adminComments}
                      onChange={(e) => setAdminComments(e.target.value)}
                      placeholder="Type comments about this merchant profile..."
                      style={{ borderRadius: 12 }}
                    />
                  ) : (
                    <Card
                      size="small"
                      style={{
                        background: "#fafafa",
                        borderRadius: 12,
                        minHeight: 80,
                        border: "1px dashed #d9d9d9",
                      }}
                    >
                      <Text style={{ whiteSpace: "pre-wrap" }}>
                        {savedAdminComments || "No administrative comments recorded."}
                      </Text>
                    </Card>
                  )}

                  <div style={{ marginTop: 12, textAlign: "right" }}>
                    {isEditingAdminComments ? (
                      <Space>
                        <Button
                          type="primary"
                          onClick={handleSaveAdminComments}
                          loading={adminCommentsUpdateLoading}
                          disabled={!isAdminCommentsChanged}
                        >
                          Save Comments
                        </Button>
                        <Button onClick={() => {
                          setAdminComments(savedAdminComments);
                          setIsEditingAdminComments(false);
                        }}>
                          Cancel
                        </Button>
                      </Space>
                    ) : (
                      <Button
                        type="dashed"
                        onClick={() => setIsEditingAdminComments(true)}
                      >
                        Edit Comments
                      </Button>
                    )}
                  </div>
                </div>
              </SurfaceCard>
            </Col>

            {/* SUBSCRIPTION SECTION */}
            <Col span={24}>
              <SurfaceCard
                bg="linear-gradient(135deg, #f8fbff 0%, #f4f8fd 100%)"
                border="1px solid #e3edf7"
              >
                {/* TOP HEADER */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 16,
                    marginBottom: 24,
                  }}
                >
                  <div>
                    <Title
                      level={4}
                      style={{
                        margin: 0,
                        fontWeight: 700,
                        color: "#0f172a",
                      }}
                    >
                      Merchant Subscription Details
                    </Title>

                    <Text
                      style={{
                        color: "#64748b",
                        fontSize: 14,
                      }}
                    >
                      Subscription plan, limits and trial configurations
                    </Text>
                  </div>

                  {canAssignSubscription && (
                    <Button
                      type="primary"
                      size="large"
                      style={{
                        borderRadius: 12,
                        height: 42,
                        fontWeight: 600,
                        paddingInline: 22,
                        boxShadow: "0 8px 18px rgba(22,119,255,.15)",
                      }}
                      onClick={() => {
                        setAssignModalVisible(true);
                        fetchActivePackages();
                      }}
                    >
                      Assign Merchant Plan
                    </Button>
                  )}
                </div>

                {/* ACTIVE PLAN */}
                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: 18,
                    border: "1px solid #edf2f7",
                    padding: 24,
                    marginBottom: 22,
                  }}
                >
                  <Row gutter={[24, 24]} align="middle">
                    <Col xs={24} lg={16}>
                      <Text
                        style={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: "#111827",
                          display: "block",
                          marginBottom: 10,
                        }}
                      >
                        Active Subscription Plan
                      </Text>

                      <Text
                        style={{
                          fontSize: 22,
                          fontWeight: 500,
                          color: "#475569",
                          display: "block",
                          marginBottom: 18,
                        }}
                      >
                        {merchant?.subscription?.name ||
                          merchant?.subscription?.package_name ||
                          merchant?.subscription?.subscription_package?.name ||
                          "No Active Plan"}
                      </Text>

                      <Space wrap size={[10, 10]}>
                        <Tag
                          color="success"
                          style={{
                            borderRadius: 999,
                            padding: "4px 14px",
                            fontWeight: 600,
                          }}
                        >
                          Active
                        </Tag>

                        {merchant?.subscription?.is_monthly_trial && (
                          <Tag
                            color="processing"
                            style={{
                              borderRadius: 999,
                              padding: "4px 14px",
                              fontWeight: 600,
                            }}
                          >
                            Monthly Trial Enabled
                          </Tag>
                        )}

                        {merchant?.subscription?.is_transaction_trial && (
                          <Tag
                            color="purple"
                            style={{
                              borderRadius: 999,
                              padding: "4px 14px",
                              fontWeight: 600,
                            }}
                          >
                            Transaction Trial Enabled
                          </Tag>
                        )}
                      </Space>
                    </Col>

                    <Col xs={24} lg={8}>
                      <div
                        style={{
                          background: "#f8fafc",
                          borderRadius: 16,
                          padding: 18,
                          border: "1px solid #e5e7eb",
                        }}
                      >
                        <Text
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: "#111827",
                            display: "block",
                            marginBottom: 8,
                          }}
                        >
                          Subscription Status
                        </Text>

                        <Text
                          style={{
                            fontSize: 16,
                            fontWeight: 500,
                            color: "#475569",
                          }}
                        >
                          Operational & Verified
                        </Text>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* LIMITS */}
                <Row gutter={[18, 18]}>
                  {[
                    {
                      title: "Daily Transaction Limit",
                      value:
                        merchant?.subscription?.daily_transaction_limit ?? "0",
                      icon: (
                        <DollarSign
                          style={{ width: 18, height: 18, color: "#14b8a6" }}
                        />
                      ),
                    },
                    {
                      title: "Daily Transaction Count",
                      value:
                        merchant?.subscription?.daily_transaction_count ?? "0",
                      icon: (
                        <RotateCcwSquare
                          style={{ width: 18, height: 18, color: "#7c3aed" }}
                        />
                      ),
                    },
                    {
                      title: "Monthly Transaction Limit",
                      value:
                        merchant?.subscription?.monthly_transaction_limit ?? "0",
                      icon: (
                        <Infinity
                          style={{ width: 18, height: 18, color: "#f59e0b" }}
                        />
                      ),
                    },
                  ].map((item, index) => (
                    <Col xs={24} md={8} key={index}>
                      <div
                        style={{
                          background: "#ffffff",
                          border: "1px solid #edf2f7",
                          borderRadius: 18,
                          padding: 22,
                          height: "100%",
                        }}
                      >
                        <div
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: 12,
                            background: "#f8fafc",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: 16,
                            border: "1px solid #eef2f7",
                          }}
                        >
                          {item.icon}
                        </div>

                        <Text
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: "#111827",
                            display: "block",
                            marginBottom: 8,
                          }}
                        >
                          {item.title}
                        </Text>

                        <Text
                          style={{
                            fontSize: 18,
                            fontWeight: 500,
                            color: "#475569",
                          }}
                        >
                          {item.value}
                        </Text>
                      </div>
                    </Col>
                  ))}
                </Row>

                {/* TRIAL SECTION */}
                <Row gutter={[18, 18]} style={{ marginTop: 18 }}>
                  <Col xs={24} md={12}>
                    <div
                      style={{
                        background: "#ffffff",
                        border: "1px solid #edf2f7",
                        borderRadius: 18,
                        padding: 22,
                        height: "100%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 14,
                        }}
                      >
                        <Text
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: "#111827",
                          }}
                        >
                          Monthly Fee Trial
                        </Text>

                        <Tag
                          color={
                            merchant?.subscription?.is_monthly_trial
                              ? "processing"
                              : "default"
                          }
                          style={{
                            borderRadius: 999,
                            padding: "3px 12px",
                            fontWeight: 600,
                          }}
                        >
                          {merchant?.subscription?.is_monthly_trial
                            ? "Enabled"
                            : "Disabled"}
                        </Tag>
                      </div>

                      <Text
                        style={{
                          fontSize: 18,
                          fontWeight: 500,
                          color: "#475569",
                          display: "block",
                          marginBottom: 8,
                        }}
                      >
                        {merchant?.subscription?.is_monthly_trial
                          ? `${merchant?.subscription?.monthly_trial_days} Days`
                          : "No Trial Available"}
                      </Text>

                      <Text
                        style={{
                          color: "#94a3b8",
                          fontSize: 13,
                          lineHeight: 1.6,
                        }}
                      >
                        Monthly subscription fee waiver configuration.
                      </Text>
                    </div>
                  </Col>

                  <Col xs={24} md={12}>
                    <div
                      style={{
                        background: "#ffffff",
                        border: "1px solid #edf2f7",
                        borderRadius: 18,
                        padding: 22,
                        height: "100%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 14,
                        }}
                      >
                        <Text
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: "#111827",
                          }}
                        >
                          Transaction Fee Trial
                        </Text>

                        <Tag
                          color={
                            merchant?.subscription?.is_transaction_trial
                              ? "purple"
                              : "default"
                          }
                          style={{
                            borderRadius: 999,
                            padding: "3px 12px",
                            fontWeight: 600,
                          }}
                        >
                          {merchant?.subscription?.is_transaction_trial
                            ? "Enabled"
                            : "Disabled"}
                        </Tag>
                      </div>

                      <Text
                        style={{
                          fontSize: 18,
                          fontWeight: 500,
                          color: "#475569",
                          display: "block",
                          marginBottom: 8,
                        }}
                      >
                        {merchant?.subscription?.is_transaction_trial
                          ? `${merchant?.subscription?.transaction_trial_days} Days`
                          : "No Trial Available"}
                      </Text>

                      <Text
                        style={{
                          color: "#94a3b8",
                          fontSize: 13,
                          lineHeight: 1.6,
                        }}
                      >
                        Transaction fee waiver configuration.
                      </Text>
                    </div>
                  </Col>
                </Row>
              </SurfaceCard>
            </Col>

            {(merchant?.directors?.length > 0 || merchant?.share_holders?.length > 0) && (
              <Col span={24}>
                <SurfaceCard bg="linear-gradient(135deg, #f0f9ff, #e6fffb)" border="1px solid #cceeff">
                  <SectionTitle icon={<UserOutlined />} title="Shareholders and Directors" />
                  <Divider />

                  <Row gutter={24}>
                    {merchant?.directors?.length > 0 && (
                      <Col span={12}>
                        <SectionTitle icon={<IdcardOutlined />} title="Directors" />
                        {merchant.directors.map((director, index) => (
                          <InfoItem key={index} value={(index + 1) + ". " + director.name} />
                        ))}
                      </Col>
                    )}

                    {merchant?.share_holders?.length > 0 && (
                      <Col span={12}>
                        <SectionTitle icon={<IdcardOutlined />} title="Shareholders (Percent Holding)" />
                        {merchant.share_holders.map((shareholder, index) => (
                          <InfoItem
                            key={index}
                            value={(index + 1) + ". " + shareholder?.name + " (" + (shareholder?.percent_holding ?? 0) + "%)"}
                          />
                        ))}
                      </Col>
                    )}
                  </Row>
                </SurfaceCard>
              </Col>
            )}

            <Col span={24}>
              <SurfaceCard bg="linear-gradient(135deg, #f0f9ff, #e6fffb)" border="1px solid #cceeff">
                <SectionTitle icon={<UserOutlined />} title={`Associated Users (${userRows?.length ?? 0})`} />
                <Divider />
                <div className="table-responsive">
                  <Table
                    dataSource={userRows}
                    columns={usersColumns}
                    pagination={false}
                    size="small"
                  />
                </div>
              </SurfaceCard>
            </Col>

            <Col span={24}>
              {walletRows.length > 0 && (
                <SurfaceCard bg="linear-gradient(135deg, #f0f9ff, #e6fffb)" border="1px solid #cceeff">
                  <SectionTitle icon={<WalletOutlined />} title="Wallet Overview" />
                  <Divider />

                  <div className="table-responsive">
                    <Table
                      dataSource={walletRows}
                      columns={walletColumns}
                      pagination={false}
                      size="small"
                    />
                  </div>
                </SurfaceCard>
              )}
            </Col>

            <Col span={24}>
              {merchant?.bank_accounts && merchant.bank_accounts.length > 0 && (
                <SurfaceCard bg="linear-gradient(135deg, #f0f9ff, #e6fffb)" border="1px solid #cceeff">
                  <SectionTitle icon={<CreditCardOutlined />} title="Linked Bank Accounts" />
                  <Divider />
                  <div className="table-responsive">
                    <Table
                      dataSource={merchant.bank_accounts}
                      columns={[
                        { title: "Bank Name", dataIndex: "bank_name", key: "bank_name" },
                        { title: "Account Holder", dataIndex: "account_holder_name", key: "account_holder_name" },
                        { title: "Account Number", dataIndex: "account_number", key: "account_number" },
                        { title: "Account Type", dataIndex: "account_type", key: "account_type" },
                        { title: "Status", dataIndex: "account_status", key: "account_status", render: (val) => <Tag color={val === "ACTIVE" ? "success" : "default"}>{val}</Tag> }
                      ]}
                      pagination={false}
                      size="small"
                      rowKey="id"
                    />
                  </div>
                </SurfaceCard>
              )}
            </Col>

            <Col span={24}>
              {merchant?.invoices && merchant.invoices.length > 0 && (
                <SurfaceCard bg="linear-gradient(135deg, #fffbe6, #fff1b8)" border="1px solid #ffe58f">
                  <SectionTitle icon={<FileTextOutlined />} title="Invoices" />
                  <Divider />
                  <div className="table-responsive">
                    <Table
                      dataSource={merchant.invoices}
                      columns={invoicesColumns}
                      pagination={false}
                      size="small"
                      rowKey="id"
                    />
                  </div>
                </SurfaceCard>
              )}
            </Col>
          </Row>
        </div>

        {/* ===== FOOTER ===== */}
        <div
          style={{
            paddingTop: 20,
            background: "#ffffff",
            borderTop: "1px solid #eaeaea",
            textAlign: "right",
          }}
        >
          <Button size="large" onClick={onClose}>
            Close
          </Button>
        </div>
      </Modal>

      {/* MANUAL SUBSCRIPTION ASSIGNMENT MODAL */}
      <Modal
        open={assignModalVisible}
        onCancel={() => setAssignModalVisible(false)}
        title="Manually Assign Merchant Subscription"
        zIndex={1100}
        destroyOnClose
        onOk={handleAssignSubscription}
        confirmLoading={assignLoading}
        okText="Assign Plan"
      >
        <div style={{ padding: "12px 0" }}>
          <Text style={{ display: "block", marginBottom: 12 }}>
            Choose from the list of active merchant subscription packages to manually assign to this merchant. No standard payment invoice or ledger transaction will be generated.
          </Text>
          <Select
            placeholder="Select active package"
            style={{ width: "100%", marginBottom: 16 }}
            value={selectedPackageId || undefined}
            onChange={handlePackageChange}
            options={packages.map(pkg => ({
              label: `${pkg.name} ($${pkg.price} / ${pkg.duration_days} days)`,
              value: pkg.id
            }))}
            getPopupContainer={(trigger) => trigger.parentNode}
          />
          
          {selectedPackageId && (
            <Card size="small" style={{ borderRadius: 12, background: "#fafafa" }}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <Text strong>Monthly Fee Free Trial</Text>
                  <Switch checked={isMonthlyTrial} onChange={(v) => setIsMonthlyTrial(v)} />
                </div>
                {isMonthlyTrial && (
                  <div>
                    <Text type="secondary">Duration (Days)</Text>
                    <Input type="number" value={monthlyTrialDays} onChange={(e) => setMonthlyTrialDays(e.target.value)} />
                  </div>
                )}
              </div>
              <Divider style={{ margin: "12px 0" }} />
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <Text strong>Transaction Fee Free Trial</Text>
                  <Switch checked={isTransactionTrial} onChange={(v) => setIsTransactionTrial(v)} />
                </div>
                {isTransactionTrial && (
                  <div>
                    <Text type="secondary">Duration (Days)</Text>
                    <Input type="number" value={transactionTrialDays} onChange={(e) => setTransactionTrialDays(e.target.value)} />
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>
      </Modal>

      <QrDownloadModal
        open={qrModalVisible}
        visible={qrModalVisible}
        onClose={() => setQrModalVisible(false)}
        qrCodeUrl={selectedWalletForQr?.qrCode}
        user={merchant}
        payId={selectedWalletForQr?.payId}
        zIndex={2000}
        downloadPng={(size) => downloadQrAsPng({ qrCodeUrl: selectedWalletForQr?.qrCode, user: merchant, payId: selectedWalletForQr?.payId, size })}
        downloadPdf={(size) => downloadQrAsPdf({ qrCodeUrl: selectedWalletForQr?.qrCode, user: merchant, payId: selectedWalletForQr?.payId, size })}
      />
    </>
  );
};

export default MerchantProfileModal;
